<script>
  import { onDestroy, onMount } from "svelte";

  import { apiMethods } from "../../Services/index.svelte";

  import {
    CalendarEventCategoryTypes,
    DirectUserRoles,
  } from "../../Utils/utilEnums.svelte";

  import {
    allGroups,
    isMobile,
    prePopulateAccountOptions,
    user,
  } from "../../stores";

  import {
    SearchInput,
    SvelteTable,
    PageLoading,
    Button,
    NotificationModal,
  } from "../../Components/Common/index.svelte";

  import {
    replaceURL,
    getText,
    showError,
    showInfo,
    loadCalendarEventCategories,
    decodeSearchParams,
    encodeSearchParams,
  } from "../../Utils/utilFunctions.svelte";

  import CalendarCategoryModal from "./CalendarCategoryModal.svelte";

  import {
    defaultCalendarEventColor,
    deleteIcon,
    editIcon,
  } from "../../Utils/utilConstants.svelte";

  import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
  import {
    cacheAccountPrePopulateSearchOptions,
    cacheAllGroups,
  } from "../../Utils/utilCache.svelte";
  import MultiSearchSelect from "../../Components/Common/MultiSearchSelect.svelte";

  export let params = {};

  let displayResults = [],
    isCategoryModalOpen = false,
    catObj = {},
    isLoading = false,
    isDeleteModalOpen = false,
    isDeleting = false,
    filters = {},
    page = 0,
    pageSize = 25,
    count,
    groupOptions = [],
    searchGroupsArr = [],
    showGroupFilter = false;

  let columns = [
    {
      key: "name",
      title: "Name",
      headerClass: "text-left px-3 rounded-tl-xl w-1/2",
      class: "text-left px-3 cursor-default",
      renderValue: (v) => getText(v.name),
    },
    {
      key: "color",
      title: "Calendar Color",
      headerClass: "text-left px-3",
      class: "text-left px-3 cursor-default",
      renderValue: (v) =>
        renderDisplayColor(v.calendarColour ? v.calendarColour : null),
      csvContent: (v) => `${v.calendarColour}`,
    },
    {
      key: "buttons",
      title: "",
      headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
      class: "px-3 cursor-default text-left",
      renderValue: (v) => _renderActionIcons(v),
      csvExportHidden: true,
    },
  ];

  $: allowGroupEdit = $user.role === DirectUserRoles.Admin;

  const groupColumn = {
    key: "groupName",
    title: "Group Name",
    headerClass: "text-left px-3 w-1/8 cursor-pointer",
    class: "text-left px-3 cursor-pointer",
    renderValue: (v) => getText(v.groupName ?? "-"),
  };

  onMount(async () => {
    await checkDoctorGroupsAndCacheData();

    if (params.query) updateSearchParamsByQuery(params.query);

    if (allowGroupEdit) columns.splice(2, 0, groupColumn);

    await loadCats(page * pageSize, pageSize, _getSearchParams());
  });

  onDestroy(() => {
    loadCalendarEventCategories();
  });

  const updateSearchParamsByQuery = (query) => {
    const arr = decodeSearchParams(query).split("&");

    if (arr.length) {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].includes("page=")) page = Number(arr[i].split("=")[1]);

        if (arr[i].includes("size=")) pageSize = Number(arr[i].split("=")[1]);

        if (arr[i].includes("search="))
          filters.search = arr[i].split("=")[1].toString();

        if (arr[i].includes("sort="))
          filters.sort = arr[i].split("=")[1].toString();

        if (arr[i].includes("groups=")) {
          searchGroupsArr = arr[i]
            .split("=")[1]
            .split(",")
            .map((id) => Number(id));
        }
      }

      updateURL();
    }
  };

  const _renderActionIcons = (c) => {
    const disabled = c.categoryType != 4;
    return `<div class="flex flex-1 items-center justify-start gap-x-2">
        <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] cursor-pointer hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Category">
          <i class="editIcon cursor-pointer ${editIcon}"></i>
      </div>

      <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] ${disabled ? "cursor-not-allowed" : "cursor-pointer"} hover:rounded-full ${disabled ? "hover:bg-grey" : "hover:bg-secondary"} ${disabled ? "text-gray-200" : ""} ${disabled ? "hover:text-grey" : "hover:text-white"}" data-tip="${!disabled ? "Delete Category" : "Global event categories cannot be deleted."}">
          <i class="deleteIcon ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${deleteIcon}"></i>
      </div>
    </div>`;
  };

  const loadCats = async (start, limit, params) => {
    isLoading = true;

    await apiMethods.getMethod
      .getCalendarEventsCategories(start, limit, params)
      .then((res) => handleResults(res));

    isLoading = false;
  };

  const handleResults = (res) => {
    if (res.error) showError(res.error);
    else {
      count = res.total;
      displayResults = [...res.results];
    }
  };

  const handleFilterSelect = () => {
    page = 0;
    updateURL();
    loadCats(0, pageSize, _getSearchParams());
  };

  const _getSearchParams = () => {
    let params = {};

    if (filters.sort) params.sort = filters.sort;

    if (filters.search) params.search = filters.search;

    if (!allowGroupEdit && $user.groupId)
      params["filter[groupId]"] = $user.groupId;
    else if (showGroupFilter && searchGroupsArr.length)
      params["filter[groupId]"] = searchGroupsArr.join(",");

    return params;
  };

  const _getParamsString = () => {
    let str = "";
    const params = _getSearchParams();

    if (params) {
      if (params.search) str += `&search=${params.search}`;
    }

    if (searchGroupsArr && searchGroupsArr.length)
      str += `&groups=${searchGroupsArr.join()}`;

    return encodeSearchParams(str);
  };

  const onPageChange = (e) => {
    page = Number(e.detail.page);

    updateURL();

    loadCats(Number(e.detail.firstDataIndex), pageSize, _getSearchParams());
  };

  const openCreateModal = () => {
    catObj = {
      categoryType: CalendarEventCategoryTypes.Custom,
      calendarColour: defaultCalendarEventColor,
    };
    isCategoryModalOpen = true;
  };

  const onPageSizeChange = (newPageSize) => {
    pageSize = newPageSize;
    page = 0;

    updateURL();

    loadCats(0, newPageSize, _getSearchParams());
  };

  const updateURL = () => {
    replaceURL(
      `/CalendarCategories/page=${page}&size=${pageSize}${_getParamsString()}`,
    );
  };

  const reloadList = () => {
    loadCats(page * pageSize, pageSize, _getSearchParams());
  };

  const renderDisplayColor = (color) => {
    if (color)
      return `
        <div
            style="background-color: ${color};
            width: 128px;
            height: 24px;
            border-radius: 5px;"
        />
      `;
    else return "-";
  };

  const handleClickCell = (event, row, key) => {
    const { className } = event.target;

    if (key == "buttons") {
      catObj = row;
      if (className.includes("deleteIcon")) 
        if(row.categoryType == 4) //delete only custom ones
          isDeleteModalOpen = true;
      else if (className.includes("editIcon")) isCategoryModalOpen = true;
    }
  };

  const proceedDelete = () => {
    if (!catObj.id) return (isDeleteModalOpen = false);
    isDeleting = true;
    apiMethods.deleteMethod
      .deleteCalendarEventsCategory(catObj.id)
      .then((res) => {
        if (res.error) throw res.error;
        else {
          isDeleting = false;
          isDeleteModalOpen = false;
          showInfo("Calendar Event Category Successfully Deleted");
          reloadList();
        }
      })
      .catch((err) => {
        isDeleting = false;
        isDeleteModalOpen = false;
        showError(err);
      });
  };

  const checkDoctorGroupsAndCacheData = async () => {
    await cacheAccountPrePopulateSearchOptions();

    if ($prePopulateAccountOptions.length) {
      showGroupFilter = $prePopulateAccountOptions.some(
        (p) => p.groupId !== $prePopulateAccountOptions[0].groupId,
      );
    }

    if (showGroupFilter) {
      await cacheAllGroups();

      groupOptions = $allGroups.map(({ id, name }) => ({
        id,
        type: name,
      }));
    }
  };
</script>

<div class="flex flex-col flex-1 gap-y-4 pb-6">
  <div
    class="flex flex-0 {$isMobile
      ? 'flex-col gap-y-4  items-start'
      : 'justify-between items-end'}"
  >
    <div class="flex gap-4 flex-wrap">
      <SearchInput
        bind:value={filters.search}
        title="Search"
        inputFunc={handleFilterSelect}
        isLong
      />

      {#if showGroupFilter}
        <MultiSearchSelect
          title="Group"
          bind:searchResult={searchGroupsArr}
          optionsArr={groupOptions}
          inputID="emailTemplateGroupSearchInput"
          onChangeFunc={handleFilterSelect}
        />
      {/if}
    </div>
    <Button clickFunc={openCreateModal}>Create Category</Button>
  </div>

  <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
    {#if isLoading}
      <PageLoading />
    {:else}
      <div
        class={$isMobile
          ? "overflow-x-auto overflow-y-visible"
          : "overflow-y-auto absolute inset-0"}
      >
        <SvelteTable
          {columns}
          rows={displayResults}
          customRow="align-middle"
          clickCellFunc={({ detail: { event, row, key } }) => {
            handleClickCell(event, row, key);
          }}
        />
      </div>
    {/if}
  </div>
</div>

<TableFooter
  {page}
  {pageSize}
  {count}
  tableData={displayResults}
  {onPageChange}
  {onPageSizeChange}
  csvConfig={{
    columns,
    data: displayResults,
    fileName: "Calendar Categories",
    allDataApi: async () =>
      await apiMethods.getMethod.getCalendarEventsCategories(
        0,
        999999,
        _getSearchParams(),
      ),
  }}
/>

<CalendarCategoryModal
  bind:catObj
  {allowGroupEdit}
  allCats={displayResults}
  bind:isModalOpen={isCategoryModalOpen}
  on:reloadList={reloadList}
/>

<NotificationModal
  title="Delete Category"
  submitText="Cancel"
  confirmButtonClass="btn-accent"
  cancelText={isDeleting ? "Deleting" : "Delete"}
  isModalOpen={isDeleteModalOpen}
  isConfirming={isDeleting}
  confirmAction={() => (isDeleteModalOpen = false)}
  closeModal={proceedDelete}
  closeModalFunction={() => (isDeleteModalOpen = false)}
  isReverseBtns={true}
>
  Delete this calendar event category?
</NotificationModal>
